import "@dxos/node-std/globals"

// packages/core/halo/credentials/src/invitations.ts
var defaultSecretProvider = async () => Buffer.from("0000");
var defaultSecretValidator = async (invitation, secret) => true;

// packages/core/halo/credentials/src/seedphrase.ts
import { generateMnemonic, mnemonicToSeedSync } from "bip39";
import assert from "@dxos/node-std/assert";
import { createKeyPair } from "@dxos/crypto";
var generateSeedPhrase = () => generateMnemonic();
var keyPairFromSeedPhrase = (seedPhrase) => {
  assert(seedPhrase);
  const seed = mnemonicToSeedSync(seedPhrase);
  return createKeyPair(seed);
};

// packages/core/halo/credentials/src/passcode.ts
var generatePasscode = (length = 4) => {
  let passcode = "";
  for (let i = 0; i < length; i++) {
    passcode += `${Math.floor(Math.random() * 10)}`;
  }
  return passcode;
};

// packages/core/halo/credentials/src/credentials/signing.ts
import stableStringify from "json-stable-stringify";
import { PublicKey } from "@dxos/keys";
var getCredentialProofPayload = (credential) => {
  const copy = {
    ...credential,
    proof: {
      ...credential.proof,
      value: new Uint8Array(),
      chain: void 0
    }
  };
  delete copy.id;
  return Buffer.from(canonicalStringify(copy));
};
var canonicalStringify = (obj) => stableStringify(obj, {
  /* The point of signing and verifying is not that the internal, private state of the objects be
  * identical, but that the public contents can be verified not to have been altered. For that reason,
  * really private fields (indicated by '__') are not included in the signature.
  * This gives a mechanism for attaching other attributes to an object without breaking the signature.
  * We also skip @type.
  */
  // TODO(dmaretskyi): Should we actually skip the @type field?
  replacer: (key, value) => {
    if (key.toString().startsWith("__") || key.toString() === "@type") {
      return void 0;
    }
    if (value) {
      if (PublicKey.isPublicKey(value)) {
        return value.toHex();
      }
      if (Buffer.isBuffer(value)) {
        return value.toString("hex");
      }
      if (value instanceof Uint8Array || value.data && value.type === "Buffer") {
        return Buffer.from(value).toString("hex");
      }
    }
    return value;
  }
});

// packages/core/halo/credentials/src/presentations/signing.ts
var getPresentationProofPayload = (credentials, proof) => {
  const copy = {
    credentials,
    proof: {
      ...proof,
      value: new Uint8Array(),
      chain: void 0
    }
  };
  return Buffer.from(canonicalStringify(copy));
};

// packages/core/halo/credentials/src/credentials/assertions.ts
var getCredentialAssertion = (credential) => credential.subject.assertion;
var isValidAuthorizedDeviceCredential = (credential, identityKey, deviceKey) => {
  const assertion = getCredentialAssertion(credential);
  return credential.subject.id.equals(deviceKey) && credential.issuer.equals(identityKey) && assertion["@type"] === "dxos.halo.credentials.AuthorizedDevice" && assertion.identityKey.equals(identityKey) && assertion.deviceKey.equals(deviceKey);
};

// packages/core/halo/credentials/src/credentials/credential-factory.ts
import assert2 from "@dxos/node-std/assert";
import { subtleCrypto } from "@dxos/crypto";
import { PublicKey as PublicKey2 } from "@dxos/keys";

// packages/core/halo/credentials/src/credentials/verifier.ts
import { verifySignature } from "@dxos/crypto";
var SIGNATURE_TYPE_ED25519 = "ED25519Signature";
var verifyCredential = async (credential) => {
  if (!credential.issuer.equals(credential.proof.signer)) {
    if (!credential.proof.chain) {
      return {
        kind: "fail",
        errors: [
          "Delegated credential is missing credential chain."
        ]
      };
    }
    const result = await verifyChain(credential.proof.chain, credential.issuer, credential.proof.signer);
    if (result.kind === "fail") {
      return result;
    }
  }
  const result1 = await verifyCredentialSignature(credential);
  if (result1.kind === "fail") {
    return result1;
  }
  return {
    kind: "pass"
  };
};
var verifyCredentialSignature = async (credential) => {
  if (credential.proof.type !== SIGNATURE_TYPE_ED25519) {
    return {
      kind: "fail",
      errors: [
        `Invalid signature type: ${credential.proof.type}`
      ]
    };
  }
  const signData = getCredentialProofPayload(credential);
  if (!await verifySignature(credential.proof.signer, signData, credential.proof.value)) {
    return {
      kind: "fail",
      errors: [
        "Invalid signature"
      ]
    };
  }
  return {
    kind: "pass"
  };
};
var verifyChain = async (chain, authority, subject) => {
  const result = await verifyCredential(chain.credential);
  if (result.kind === "fail") {
    return result;
  }
  if (!isValidAuthorizedDeviceCredential(chain.credential, authority, subject)) {
    return {
      kind: "fail",
      errors: [
        `Invalid credential chain: invalid assertion for key: ${subject}`
      ]
    };
  }
  return {
    kind: "pass"
  };
};

// packages/core/halo/credentials/src/credentials/credential-factory.ts
var createCredential = async ({ signer, issuer, subject, assertion, signingKey, chain, nonce }) => {
  assert2(assertion["@type"], "Invalid assertion.");
  assert2(!!signingKey === !!chain, "Chain must be provided if and only if the signing key differs from the issuer.");
  if (chain) {
    const result = await verifyChain(chain, issuer, signingKey);
    assert2(result.kind === "pass", "Invalid chain.");
  }
  const credential = {
    issuer,
    issuanceDate: new Date(),
    subject: {
      id: subject,
      assertion
    },
    proof: {
      type: SIGNATURE_TYPE_ED25519,
      creationDate: new Date(),
      signer: signingKey != null ? signingKey : issuer,
      value: new Uint8Array(),
      nonce
    }
  };
  const signedPayload = getCredentialProofPayload(credential);
  credential.proof.value = await signer.sign(signingKey != null ? signingKey : issuer, signedPayload);
  if (chain) {
    credential.proof.chain = chain;
  }
  credential.id = PublicKey2.from(await subtleCrypto.digest("SHA-256", signedPayload));
  return credential;
};
var createCredentialMessage = (credential) => {
  return {
    "@type": "dxos.echo.feed.CredentialsMessage",
    credential
  };
};
var createCredentialSignerWithKey = (signer, issuer) => ({
  getIssuer: () => issuer,
  createCredential: ({ subject, assertion, nonce }) => createCredential({
    signer,
    issuer,
    subject,
    assertion,
    nonce
  })
});
var createCredentialSignerWithChain = (signer, chain, signingKey) => ({
  getIssuer: () => chain.credential.issuer,
  createCredential: ({ subject, assertion, nonce }) => createCredential({
    signer,
    issuer: chain.credential.issuer,
    signingKey,
    chain,
    subject,
    assertion,
    nonce
  })
});

// packages/core/halo/credentials/src/credentials/credential-generator.ts
import { AdmittedFeed, SpaceMember } from "@dxos/protocols/proto/dxos/halo/credentials";
var CredentialGenerator = class {
  constructor(_signer, _identityKey, _deviceKey) {
    this._signer = _signer;
    this._identityKey = _identityKey;
    this._deviceKey = _deviceKey;
  }
  /**
  * Create genesis messages for new Space.
  */
  async createSpaceGenesis(spaceKey, controlKey, creatorProfile) {
    return [
      await createCredential({
        signer: this._signer,
        issuer: spaceKey,
        subject: spaceKey,
        assertion: {
          "@type": "dxos.halo.credentials.SpaceGenesis",
          spaceKey
        }
      }),
      await createCredential({
        signer: this._signer,
        issuer: spaceKey,
        subject: this._identityKey,
        assertion: {
          "@type": "dxos.halo.credentials.SpaceMember",
          spaceKey,
          role: SpaceMember.Role.ADMIN,
          profile: creatorProfile,
          genesisFeedKey: controlKey
        }
      }),
      await this.createFeedAdmission(spaceKey, controlKey, AdmittedFeed.Designation.CONTROL)
    ];
  }
  /**
  * Create invitation.
  * Admit identity and control and data feeds.
  */
  // TODO(burdon): Reconcile with above (esp. Signer).
  async createMemberInvitation(spaceKey, identityKey, deviceKey, controlKey, dataKey, genesisFeedKey) {
    return [
      await createCredential({
        signer: this._signer,
        issuer: this._identityKey,
        subject: identityKey,
        assertion: {
          "@type": "dxos.halo.credentials.SpaceMember",
          spaceKey,
          role: SpaceMember.Role.MEMBER,
          genesisFeedKey
        }
      }),
      await this.createFeedAdmission(spaceKey, controlKey, AdmittedFeed.Designation.CONTROL),
      await this.createFeedAdmission(spaceKey, dataKey, AdmittedFeed.Designation.DATA)
    ];
  }
  /**
  * Add device to space.
  */
  // TODO(burdon): Reconcile with below.
  async createDeviceAuthorization(deviceKey) {
    return createCredential({
      signer: this._signer,
      issuer: this._identityKey,
      subject: deviceKey,
      assertion: {
        "@type": "dxos.halo.credentials.AuthorizedDevice",
        identityKey: this._identityKey,
        deviceKey
      }
    });
  }
  /**
  * Add feed to space.
  */
  async createFeedAdmission(spaceKey, feedKey, designation) {
    return createCredential({
      signer: this._signer,
      issuer: this._identityKey,
      subject: feedKey,
      assertion: {
        "@type": "dxos.halo.credentials.AdmittedFeed",
        spaceKey,
        identityKey: this._identityKey,
        deviceKey: this._deviceKey,
        designation
      }
    });
  }
  async createProfileCredential(profile) {
    return createCredential({
      signer: this._signer,
      issuer: this._identityKey,
      subject: this._identityKey,
      assertion: {
        "@type": "dxos.halo.credentials.IdentityProfile",
        profile
      }
    });
  }
};
var createDeviceAuthorization = async (signer, identityKey, deviceKey) => {
  const credentials = await Promise.all([
    await signer.createCredential({
      subject: deviceKey,
      assertion: {
        "@type": "dxos.halo.credentials.AuthorizedDevice",
        identityKey,
        deviceKey
      }
    })
  ]);
  return credentials.map((credential) => ({
    "@type": "dxos.echo.feed.CredentialsMessage",
    credential
  }));
};
var createAdmissionCredentials = async (signer, identityKey, spaceKey, genesisFeedKey, profile) => {
  const credentials = await Promise.all([
    await signer.createCredential({
      subject: identityKey,
      assertion: {
        "@type": "dxos.halo.credentials.SpaceMember",
        spaceKey,
        role: SpaceMember.Role.ADMIN,
        profile,
        genesisFeedKey
      }
    })
  ]);
  return credentials.map((credential) => ({
    credential: {
      credential
    }
  }));
};

// packages/core/halo/credentials/src/presentations/presentation.ts
var signPresentation = async ({ presentation, signer, signerKey, chain, nonce }) => {
  var _a, _b;
  const proof = {
    type: SIGNATURE_TYPE_ED25519,
    value: new Uint8Array(),
    creationDate: new Date(),
    signer: signerKey,
    nonce
  };
  const signedPayload = getPresentationProofPayload((_a = presentation.credentials) != null ? _a : [], proof);
  proof.value = await signer.sign(signerKey, signedPayload);
  if (chain) {
    proof.chain = chain;
  }
  return {
    credentials: presentation.credentials,
    proofs: [
      ...(_b = presentation.proofs) != null ? _b : [],
      proof
    ]
  };
};

// packages/core/halo/credentials/src/presentations/verifier.ts
import { verifySignature as verifySignature2 } from "@dxos/crypto";
var verifyPresentation = async (presentation) => {
  var _a, _b, _c, _d;
  const errors = [];
  const credentialsVerifications = await Promise.all((_b = (_a = presentation.credentials) == null ? void 0 : _a.map((credential) => verifyCredential(credential))) != null ? _b : []);
  for (const verification of credentialsVerifications) {
    if (verification.kind === "fail") {
      errors.push(...verification.errors);
    }
  }
  const proofVerification = await Promise.all((_d = (_c = presentation.proofs) == null ? void 0 : _c.map(async (proof) => {
    const chainVerification = await verifyPresentationChain(presentation, proof);
    if (chainVerification.kind === "fail") {
      return chainVerification;
    }
    const signatureVerification = await verifyPresentationSignature(presentation, proof);
    if (signatureVerification.kind === "fail") {
      return signatureVerification;
    }
    return {
      kind: "pass"
    };
  })) != null ? _d : []);
  for (const verification1 of proofVerification) {
    if (verification1.kind === "fail") {
      errors.push(...verification1.errors);
    }
  }
  if (errors.length === 0) {
    return {
      kind: "pass"
    };
  }
  {
    return {
      kind: "fail",
      errors
    };
  }
};
var verifyPresentationChain = async (presentation, proof) => {
  var _a;
  for (const credential of (_a = presentation.credentials) != null ? _a : []) {
    if (!credential.issuer.equals(proof.signer)) {
      if (!proof.chain) {
        return {
          kind: "fail",
          errors: [
            "Delegated credential is missing credential chain."
          ]
        };
      }
      const chainVerification = await verifyChain(proof.chain, credential.issuer, proof.signer);
      if (chainVerification.kind === "fail") {
        return chainVerification;
      }
    }
  }
  return {
    kind: "pass"
  };
};
var verifyPresentationSignature = async (presentation, proof) => {
  var _a;
  if (proof.type !== SIGNATURE_TYPE_ED25519) {
    return {
      kind: "fail",
      errors: [
        `Invalid signature type: ${proof.type}`
      ]
    };
  }
  const signData = getPresentationProofPayload((_a = presentation.credentials) != null ? _a : [], proof);
  if (!await verifySignature2(proof.signer, signData, proof.value)) {
    return {
      kind: "fail",
      errors: [
        "Invalid signature"
      ]
    };
  }
  return {
    kind: "pass"
  };
};

// packages/core/halo/credentials/src/state-machine/space-state-machine.ts
import { PublicKey as PublicKey5 } from "@dxos/keys";
import { log as log2 } from "@dxos/log";
import { SpaceMember as SpaceMember2 } from "@dxos/protocols/proto/dxos/halo/credentials";
import { Callback as Callback3, ComplexSet } from "@dxos/util";

// packages/core/halo/credentials/src/processor/credential-processor.ts
import { runInContextAsync } from "@dxos/async";
import { Context } from "@dxos/context";
var CredentialConsumer = class {
  constructor(processor, _onOpen, _onClose) {
    this.processor = processor;
    this._onOpen = _onOpen;
    this._onClose = _onClose;
    this._ctx = new Context();
    this._isReadyForLiveCredentials = false;
  }
  /**
  * @internal
  */
  async _process(credential) {
    await runInContextAsync(this._ctx, async () => {
      await this.processor.process(credential);
    });
  }
  async open() {
    if (this._ctx.disposed) {
      throw new Error("CredentialProcessor is disposed");
    }
    await this._onOpen();
  }
  async close() {
    await this._ctx.dispose();
    await this._onClose();
  }
};

// packages/core/halo/credentials/src/state-machine/feed-state-machine.ts
import assert3 from "@dxos/node-std/assert";
import { PublicKey as PublicKey3 } from "@dxos/keys";
import { Callback, ComplexMap } from "@dxos/util";
var FeedStateMachine = class {
  // prettier-ignore
  constructor(_spaceKey) {
    this._spaceKey = _spaceKey;
    this._feeds = new ComplexMap(PublicKey3.hash);
    this.onFeedAdmitted = new Callback();
  }
  get feeds() {
    return this._feeds;
  }
  /**
  * Processes the AdmittedFeed credential.
  * Assumes the credential is already pre-verified
  * and the issuer has been authorized to issue credentials of this type.
  * @param fromFeed Key of the feed where this credential is recorded.
  */
  async process(credential, fromFeed) {
    const assertion = getCredentialAssertion(credential);
    assert3(assertion["@type"] === "dxos.halo.credentials.AdmittedFeed");
    assert3(assertion.spaceKey.equals(this._spaceKey));
    const info = {
      key: credential.subject.id,
      credential,
      assertion,
      parent: fromFeed
    };
    this._feeds.set(credential.subject.id, info);
    await this.onFeedAdmitted.callIfSet(info);
  }
};

// packages/core/halo/credentials/src/state-machine/member-state-machine.ts
import assert4 from "@dxos/node-std/assert";
import { PublicKey as PublicKey4 } from "@dxos/keys";
import { log } from "@dxos/log";
import { Callback as Callback2, ComplexMap as ComplexMap2 } from "@dxos/util";
var MemberStateMachine = class {
  // prettier-ignore
  constructor(_spaceKey) {
    this._spaceKey = _spaceKey;
    this._members = new ComplexMap2(PublicKey4.hash);
    this.onMemberAdmitted = new Callback2();
  }
  get members() {
    return this._members;
  }
  getRole(member) {
    var _a;
    return (_a = this._members.get(member)) == null ? void 0 : _a.assertion.role;
  }
  /**
  * Processes the SpaceMember credential.
  * Assumes the credential is already pre-verified
  * and the issuer has been authorized to issue credentials of this type.
  * @param fromFeed Key of the feed where this credential is recorded.
  */
  async process(credential) {
    const assertion = getCredentialAssertion(credential);
    assert4(assertion["@type"] === "dxos.halo.credentials.SpaceMember");
    assert4(assertion.spaceKey.equals(this._spaceKey));
    assert4(!this._members.has(credential.subject.id), `Duplicate SpaceMember credential: ${credential.subject.id}`);
    const info = {
      key: credential.subject.id,
      credential,
      assertion
    };
    this._members.set(credential.subject.id, info);
    log("member added", {
      member: credential.subject.id,
      space: this._spaceKey,
      role: assertion.role,
      profile: assertion.profile
    }, {
      file: "member-state-machine.ts",
      line: 63,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await this.onMemberAdmitted.callIfSet(info);
  }
};

// packages/core/halo/credentials/src/state-machine/space-state-machine.ts
var SpaceStateMachine = class {
  // prettier-ignore
  constructor(_spaceKey) {
    this._spaceKey = _spaceKey;
    this._members = new MemberStateMachine(this._spaceKey);
    this._feeds = new FeedStateMachine(this._spaceKey);
    this._credentials = [];
    this._processedCredentials = new ComplexSet(PublicKey5.hash);
    this._credentialProcessors = [];
    this.onCredentialProcessed = new Callback3();
    this.onMemberAdmitted = this._members.onMemberAdmitted;
    this.onFeedAdmitted = this._feeds.onFeedAdmitted;
  }
  // TODO(burdon): Return state object rather than extend.
  get members() {
    return this._members.members;
  }
  get feeds() {
    return this._feeds.feeds;
  }
  get credentials() {
    return this._credentials;
  }
  get genesisCredential() {
    return this._genesisCredential;
  }
  registerProcessor(handler) {
    const processor = new CredentialConsumer(handler, async () => {
      for (const credential of this._credentials) {
        await processor._process(credential);
      }
      processor._isReadyForLiveCredentials = true;
    }, async () => {
      this._credentialProcessors = this._credentialProcessors.filter((p) => p !== processor);
    });
    this._credentialProcessors.push(processor);
    return processor;
  }
  getCredentialsOfType(type) {
    return this._credentials.filter((credential) => getCredentialAssertion(credential)["@type"] === type);
  }
  /**
  * @param fromFeed Key of the feed where this credential is recorded.
  */
  async process(credential, fromFeed) {
    if (credential.id) {
      if (this._processedCredentials.has(credential.id)) {
        log2.warn("duplicate credential", {
          id: credential.id,
          type: getCredentialAssertion(credential)["@type"]
        }, {
          file: "space-state-machine.ts",
          line: 99,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        return false;
      }
      this._processedCredentials.add(credential.id);
    }
    const result = await verifyCredential(credential);
    if (result.kind !== "pass") {
      log2.warn(`Invalid credential: ${result.errors.join(", ")}`, {}, {
        file: "space-state-machine.ts",
        line: 110,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      return false;
    }
    const assertion = getCredentialAssertion(credential);
    switch (assertion["@type"]) {
      case "dxos.halo.credentials.SpaceGenesis": {
        if (this._genesisCredential) {
          log2.warn("Space already has a genesis credential.", {}, {
            file: "space-state-machine.ts",
            line: 118,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          return false;
        }
        if (!credential.issuer.equals(this._spaceKey)) {
          log2.warn("Space genesis credential must be issued by space.", {}, {
            file: "space-state-machine.ts",
            line: 122,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          return false;
        }
        if (!credential.subject.id.equals(this._spaceKey)) {
          log2.warn("Space genesis credential must be issued to space.", {}, {
            file: "space-state-machine.ts",
            line: 126,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          return false;
        }
        this._genesisCredential = credential;
        break;
      }
      case "dxos.halo.credentials.SpaceMember": {
        if (!assertion.spaceKey.equals(this._spaceKey)) {
          break;
        }
        if (!this._genesisCredential) {
          log2.warn("Space must have a genesis credential before adding members.", {}, {
            file: "space-state-machine.ts",
            line: 140,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          return false;
        }
        if (!this._canInviteNewMembers(credential.issuer)) {
          log2.warn(`Space member is not authorized to invite new members: ${credential.issuer}`, {}, {
            file: "space-state-machine.ts",
            line: 144,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          return false;
        }
        await this._members.process(credential);
        break;
      }
      case "dxos.halo.credentials.AdmittedFeed": {
        if (!this._genesisCredential) {
          log2.warn("Space must have a genesis credential before admitting feeds.", {}, {
            file: "space-state-machine.ts",
            line: 154,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          return false;
        }
        if (!this._canAdmitFeeds(credential.issuer)) {
          log2.warn(`Space member is not authorized to admit feeds: ${credential.issuer}`, {}, {
            file: "space-state-machine.ts",
            line: 158,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          return false;
        }
        await this._feeds.process(credential, fromFeed);
        break;
      }
    }
    void this._credentials.push(credential);
    for (const processor of this._credentialProcessors) {
      if (processor._isReadyForLiveCredentials) {
        await processor._process(credential);
      }
    }
    await this.onCredentialProcessed.callIfSet(credential);
    return true;
  }
  _canInviteNewMembers(key) {
    return key.equals(this._spaceKey) || this._members.getRole(key) === SpaceMember2.Role.ADMIN;
  }
  _canAdmitFeeds(key) {
    const role = this._members.getRole(key);
    return role === SpaceMember2.Role.MEMBER || role === SpaceMember2.Role.ADMIN;
  }
};

// packages/core/halo/credentials/src/processor/device-state-machine.ts
import { Trigger } from "@dxos/async";
import { PublicKey as PublicKey6 } from "@dxos/keys";
import { log as log3 } from "@dxos/log";
import { ComplexSet as ComplexSet2 } from "@dxos/util";
var DeviceStateMachine = class {
  // prettier-ignore
  constructor(_params) {
    this._params = _params;
    this.authorizedDeviceKeys = new ComplexSet2(PublicKey6.hash);
    this.deviceChainReady = new Trigger();
  }
  async process(credential) {
    var _a, _b;
    log3("processing credential...", {
      identityKey: this._params.identityKey,
      deviceKey: this._params.deviceKey,
      credential
    }, {
      file: "device-state-machine.ts",
      line: 36,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    if (isValidAuthorizedDeviceCredential(credential, this._params.identityKey, this._params.deviceKey)) {
      this.deviceCredentialChain = {
        credential
      };
      this.deviceChainReady.wake();
    }
    const assertion = getCredentialAssertion(credential);
    switch (assertion["@type"]) {
      case "dxos.halo.credentials.AuthorizedDevice": {
        this.authorizedDeviceKeys.add(assertion.deviceKey);
        log3("added device", {
          localDeviceKey: this._params.deviceKey,
          deviceKey: assertion.deviceKey,
          size: this.authorizedDeviceKeys.size
        }, {
          file: "device-state-machine.ts",
          line: 53,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        (_b = (_a = this._params).onUpdate) == null ? void 0 : _b.call(_a);
        break;
      }
    }
  }
};

// packages/core/halo/credentials/src/processor/profile-state-machine.ts
import { log as log4 } from "@dxos/log";
var ProfileStateMachine = class {
  // prettier-ignore
  constructor(_params) {
    this._params = _params;
  }
  async process(credential) {
    var _a, _b;
    const assertion = getCredentialAssertion(credential);
    switch (assertion["@type"]) {
      case "dxos.halo.credentials.IdentityProfile": {
        if (!credential.issuer.equals(this._params.identityKey) || !credential.subject.id.equals(this._params.identityKey)) {
          log4.warn("Invalid profile credential", {
            expectedIdentity: this._params.identityKey,
            credential
          }, {
            file: "profile-state-machine.ts",
            line: 37,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          return;
        }
        this.profile = assertion.profile;
        log4("updated profile", {
          identityKey: this._params.identityKey,
          profile: this.profile
        }, {
          file: "profile-state-machine.ts",
          line: 43,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        (_b = (_a = this._params).onUpdate) == null ? void 0 : _b.call(_a);
        break;
      }
    }
  }
};
export {
  CredentialConsumer,
  CredentialGenerator,
  DeviceStateMachine,
  FeedStateMachine,
  MemberStateMachine,
  ProfileStateMachine,
  SIGNATURE_TYPE_ED25519,
  SpaceStateMachine,
  canonicalStringify,
  createAdmissionCredentials,
  createCredential,
  createCredentialMessage,
  createCredentialSignerWithChain,
  createCredentialSignerWithKey,
  createDeviceAuthorization,
  defaultSecretProvider,
  defaultSecretValidator,
  generatePasscode,
  generateSeedPhrase,
  getCredentialAssertion,
  getCredentialProofPayload,
  getPresentationProofPayload,
  isValidAuthorizedDeviceCredential,
  keyPairFromSeedPhrase,
  signPresentation,
  verifyChain,
  verifyCredential,
  verifyCredentialSignature,
  verifyPresentation,
  verifyPresentationChain,
  verifyPresentationSignature
};
//# sourceMappingURL=index.mjs.map
